import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function FeatureCollectionModal({ open, onClose, items = [] }) {
  const [form, setForm] = React.useState({
    fullName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    collectionName: "",
    notes: "",
    xHandle: "",
    discord: "",
    mintPage: "",
    logoFile: null, // required
    bannerFile: null,
  });

  const [sending, setSending] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFile = (k) => (e) => {
    const file = e.target.files?.[0] || null;
    setForm((f) => ({ ...f, [k]: file }));
  };

  const validAccount =
    form.fullName.trim() &&
    form.username.trim() &&
    form.email.trim() &&
    form.password.length >= 6 &&
    form.password === form.confirmPassword;

  const validCreator = form.collectionName.trim() && form.logoFile;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validAccount || !validCreator) return;
    setSending(true);
    try {
      // TODO: integrate with Firestore / backend
      // FormData example:
      // const fd = new FormData();
      // Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      // await api.submitFeatureInterest(fd);

      await new Promise((r) => setTimeout(r, 700));
      setDone(true);
    } catch (err) {
      console.error("feature_collection_submit_error", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="feature-collection-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "#0b0620",
          border: "1px solid rgba(255,255,255,.08)",
        },
      }}
    >
      <DialogTitle
        id="feature-collection-title"
        sx={{ pr: 6, color: "#fff", fontWeight: 800 }}
      >
        Feature Your Collection
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          color: "#eae9ff",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0) 60%)",
        }}
      >
        {/* Overview – aligns with Season 2 Enrollment steps */}
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ mb: 1, fontWeight: 700, color: "#fff" }}>
            Season 2 Enrollment
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Sign up through the enrollment portal, share your referral link,
            then get your NFTs in-game and earn revenue.
          </Typography>
          {/* Source: Season 2 deck (Sign up → Share → Get NFTs in game & earn revenue) :contentReference[oaicite:1]{index=1} */}
        </Box>

        {/* Highlights grid (same “parent grid” info you show on onboarding) */}
        {!!items.length && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {items.map((it) => (
              <Grid key={it.id} item xs={12} sm={6} md={4}>
                <Box
                  role="group"
                  aria-label={it.title}
                  sx={{
                    height: "100%",
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    {typeof it.icon === "string" ? (
                      <img
                        src={it.icon}
                        alt=""
                        aria-hidden
                        width={28}
                        height={28}
                        style={{ objectFit: "contain" }}
                      />
                    ) : (
                      it.icon
                    )}
                    <Typography sx={{ fontWeight: 800, color: "#fff" }}>
                      {it.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ opacity: 0.95 }}>
                    {it.body}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Account creation (matches onboarding account fields) */}
        {!done && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              mb: 3,
            }}
          >
            <Typography sx={{ mb: 2, fontWeight: 700, color: "#fff" }}>
              Create Your Account
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name"
                  value={form.fullName}
                  onChange={set("fullName")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Username (Gamertag)"
                  value={form.username}
                  onChange={set("username")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="Email"
                  value={form.email}
                  onChange={set("email")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Password"
                  value={form.password}
                  onChange={set("password")}
                  helperText="6+ characters"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Confirm Password"
                  value={form.confirmPassword}
                  onChange={set("confirmPassword")}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Collection Creator (matches onboarding creator fields) */}
        {!done && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <Typography sx={{ mb: 2, fontWeight: 700, color: "#fff" }}>
              Collection Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name of Collection"
                  value={form.collectionName}
                  onChange={set("collectionName")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="X User Handle"
                  value={form.xHandle}
                  onChange={set("xHandle")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Discord (optional)"
                  placeholder="discord.gg/your-server"
                  value={form.discord}
                  onChange={set("discord")}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Mint Page / NFT Link (optional)"
                  placeholder="opensea.io/collection/your-collection"
                  value={form.mintPage}
                  onChange={set("mintPage")}
                />
              </Grid>

              {/* Logo (required) */}
              <Grid item xs={12}>
                <Typography sx={{ color: "#fff", mb: 1, fontWeight: 700 }}>
                  Collection Logo (required)
                </Typography>
                <Button
                  component="label"
                  variant="contained"
                  sx={{ color: "#fff", borderColor: "#fff" }}
                >
                  Upload Logo
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFile("logoFile")}
                  />
                </Button>
                {form.logoFile && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Avatar
                      src={URL.createObjectURL(form.logoFile)}
                      alt="logo preview"
                      sx={{
                        width: 56,
                        height: 56,
                        border: "2px solid #6f52ed",
                      }}
                    />
                    <Typography sx={{ color: "#ddd" }}>
                      {form.logoFile.name}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Banner (optional) */}
              <Grid item xs={12}>
                <Typography sx={{ color: "#fff", mb: 1, fontWeight: 700 }}>
                  Banner (optional)
                </Typography>
                <Button
                  component="label"
                  variant="contained"
                  sx={{ color: "#fff", borderColor: "#fff" }}
                >
                  Upload Banner
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFile("bannerFile")}
                  />
                </Button>
                {form.bannerFile && (
                  <Box sx={{ mt: 1 }}>
                    <img
                      src={URL.createObjectURL(form.bannerFile)}
                      alt="banner preview"
                      style={{
                        width: "100%",
                        maxHeight: 140,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "2px solid #6f52ed",
                      }}
                    />
                    <Typography sx={{ color: "#ddd", mt: 0.5 }}>
                      {form.bannerFile.name}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Success state */}
        {done && (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ color: "#b6ffcc", fontWeight: 700 }}>
              Thanks! We got your note — we’ll reach out shortly.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {!done ? (
          <>
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={sending || !validAccount || !validCreator}
            >
              {sending ? "Sending…" : "Send"}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
